import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ExternalLink, 
  AlertCircle,
  FileText,
  Layers,
  ListTodo
} from 'lucide-react';
import { toast } from 'sonner';
import type { JiraCredentials, JiraBacklogOptions, JiraBacklogPreview, ConfigPhase } from '../types';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface JiraBacklogGeneratorProps {
  companyId: string;
  companyName: string;
  accessToken: string;
}

export function JiraBacklogGenerator({ companyId, companyName, accessToken }: JiraBacklogGeneratorProps) {
  const [step, setStep] = useState<'credentials' | 'options' | 'preview' | 'generating' | 'complete'>('credentials');
  
  // Credentials state
  const [credentials, setCredentials] = useState<JiraCredentials>({
    jiraUrl: '',
    email: '',
    apiToken: '',
    projectKey: ''
  });
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [projectName, setProjectName] = useState('');

  // Options state
  const [options, setOptions] = useState<JiraBacklogOptions>({
    includeAllPhases: true,
    selectedPhases: [1, 2, 3, 4],
    onlyEnabledItems: true,
    createSubtasks: true,
    assignToConsultants: true,
    addAcceptanceCriteria: true,
    onlyReadyItems: false
  });

  // Preview state
  const [preview, setPreview] = useState<JiraBacklogPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationResult, setGenerationResult] = useState<any>(null);

  // Load existing credentials
  useEffect(() => {
    loadExistingCredentials();
  }, [companyId]);

  const loadExistingCredentials = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/jira/credentials/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.hasCredentials) {
          setHasExistingCredentials(true);
          setCredentials(prev => ({
            ...prev,
            jiraUrl: data.jiraUrl,
            email: data.email,
            projectKey: data.projectKey
          }));
        }
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    }
  };

  const testConnection = async () => {
    if (!credentials.jiraUrl || !credentials.email || !credentials.apiToken || !credentials.projectKey) {
      toast.error('Please fill in all connection fields');
      return;
    }

    setTestingConnection(true);
    setConnectionSuccess(false);
    setConnectionError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/jira/test-connection`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(credentials)
        }
      );

      const data = await response.json();

      if (data.success) {
        setConnectionSuccess(true);
        setProjectName(data.projectName);
        toast.success(`Connected to Jira project: ${data.projectName}`);
      } else {
        setConnectionError(data.error || 'Connection failed');
        toast.error(data.error || 'Connection failed');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to connect to Jira';
      setConnectionError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setTestingConnection(false);
    }
  };

  const saveCredentials = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/jira/save-credentials`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            companyId,
            ...credentials
          })
        }
      );

      if (response.ok) {
        toast.success('Credentials saved successfully');
        setHasExistingCredentials(true);
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save credentials');
      }
    } catch (error) {
      toast.error('Failed to save credentials');
    }
  };

  const handleNextToOptions = async () => {
    if (!connectionSuccess) {
      toast.error('Please test the connection first');
      return;
    }
    
    // Save credentials
    await saveCredentials();
    setStep('options');
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/jira/preview-backlog`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            companyId,
            options
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
        setStep('preview');
      } else {
        toast.error('Failed to load preview');
      }
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const generateBacklog = async () => {
    setGenerating(true);
    setGenerationProgress(0);
    setStep('generating');

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 5, 90));
      }, 500);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/jira/generate-backlog`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            companyId,
            credentials,
            options
          })
        }
      );

      clearInterval(progressInterval);

      if (response.ok) {
        const data = await response.json();
        setGenerationProgress(100);
        setGenerationResult(data);
        setStep('complete');
        
        if (data.success) {
          toast.success('Jira backlog generated');
        } else {
          toast.error('Backlog generation completed with errors');
        }
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to generate backlog');
        setStep('preview');
      }
    } catch (error) {
      toast.error('Failed to generate backlog');
      setStep('preview');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate Jira Implementation Backlog</CardTitle>
          <CardDescription>
            Create a complete backlog in Jira Software with Epics, Stories, and Subtasks for {companyName}'s JSM implementation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className={`flex items-center space-x-2 ${step === 'credentials' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step === 'credentials' ? 'border-blue-600 bg-blue-50' : 'border-green-600 bg-green-50'}`}>
                {step !== 'credentials' ? <CheckCircle2 className="w-5 h-5" /> : '1'}
              </div>
              <span className="text-sm">Connection</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-2" />
            <div className={`flex items-center space-x-2 ${step === 'options' ? 'text-blue-600' : (step === 'preview' || step === 'generating' || step === 'complete') ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step === 'options' ? 'border-blue-600 bg-blue-50' : (step === 'preview' || step === 'generating' || step === 'complete') ? 'border-green-600 bg-green-50' : 'border-gray-300'}`}>
                {(step === 'preview' || step === 'generating' || step === 'complete') ? <CheckCircle2 className="w-5 h-5" /> : '2'}
              </div>
              <span className="text-sm">Options</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-2" />
            <div className={`flex items-center space-x-2 ${step === 'preview' ? 'text-blue-600' : (step === 'generating' || step === 'complete') ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step === 'preview' ? 'border-blue-600 bg-blue-50' : (step === 'generating' || step === 'complete') ? 'border-green-600 bg-green-50' : 'border-gray-300'}`}>
                {(step === 'generating' || step === 'complete') ? <CheckCircle2 className="w-5 h-5" /> : '3'}
              </div>
              <span className="text-sm">Preview</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-2" />
            <div className={`flex items-center space-x-2 ${step === 'complete' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step === 'complete' ? 'border-green-600 bg-green-50' : 'border-gray-300'}`}>
                {step === 'complete' ? <CheckCircle2 className="w-5 h-5" /> : '4'}
              </div>
              <span className="text-sm">Complete</span>
            </div>
          </div>

          {/* Step 1: Credentials */}
          {step === 'credentials' && (
            <div className="space-y-4">
              {hasExistingCredentials && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Saved credentials found. You can update them or use existing credentials.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="jiraUrl">Jira URL *</Label>
                <Input
                  id="jiraUrl"
                  placeholder="https://company.atlassian.net"
                  value={credentials.jiraUrl}
                  onChange={(e) => setCredentials({ ...credentials, jiraUrl: e.target.value })}
                />
                <p className="text-sm text-gray-500">Your Jira Cloud instance URL</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectKey">Project Key *</Label>
                <Input
                  id="projectKey"
                  placeholder="IMPL"
                  value={credentials.projectKey}
                  onChange={(e) => setCredentials({ ...credentials, projectKey: e.target.value.toUpperCase() })}
                />
                <p className="text-sm text-gray-500">The Jira Software project key where backlog will be created</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="consultant@company.com"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                />
                <p className="text-sm text-gray-500">Your Jira account email</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiToken">API Token *</Label>
                <Input
                  id="apiToken"
                  type="password"
                  placeholder="************************"
                  value={credentials.apiToken}
                  onChange={(e) => setCredentials({ ...credentials, apiToken: e.target.value })}
                />
                <p className="text-sm text-gray-500">
                  <a 
                    href="https://id.atlassian.com/manage-profile/security/api-tokens" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    Generate an API token <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Button
                  onClick={testConnection}
                  disabled={testingConnection}
                  variant="outline"
                >
                  {testingConnection ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>

                <Button
                  onClick={handleNextToOptions}
                  disabled={!connectionSuccess}
                >
                  Next: Configure Options
                </Button>
              </div>

              {connectionSuccess && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Successfully connected to Jira project: <strong>{projectName}</strong>
                  </AlertDescription>
                </Alert>
              )}

              {connectionError && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{connectionError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Step 2: Options */}
          {step === 'options' && (
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeAllPhases"
                    checked={options.includeAllPhases}
                    onCheckedChange={(checked) => setOptions({ 
                      ...options, 
                      includeAllPhases: checked as boolean,
                      selectedPhases: checked ? [1, 2, 3, 4] : []
                    })}
                  />
                  <Label htmlFor="includeAllPhases">Include all phases (1-4)</Label>
                </div>

                {!options.includeAllPhases && (
                  <div className="ml-6 space-y-2">
                    <Label>Select phases to include:</Label>
                    {[1, 2, 3, 4].map(phase => (
                      <div key={phase} className="flex items-center space-x-2">
                        <Checkbox
                          id={`phase${phase}`}
                          checked={options.selectedPhases?.includes(phase as ConfigPhase)}
                          onCheckedChange={(checked) => {
                            const current = options.selectedPhases || [];
                            setOptions({
                              ...options,
                              selectedPhases: checked
                                ? [...current, phase as ConfigPhase]
                                : current.filter(p => p !== phase)
                            });
                          }}
                        />
                        <Label htmlFor={`phase${phase}`}>Phase {phase}</Label>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="onlyEnabledItems"
                    checked={options.onlyEnabledItems}
                    onCheckedChange={(checked) => setOptions({ ...options, onlyEnabledItems: checked as boolean })}
                  />
                  <Label htmlFor="onlyEnabledItems">Only include enabled config items</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="createSubtasks"
                    checked={options.createSubtasks}
                    onCheckedChange={(checked) => setOptions({ ...options, createSubtasks: checked as boolean })}
                  />
                  <Label htmlFor="createSubtasks">Create subtasks for detailed implementation steps</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="assignToConsultants"
                    checked={options.assignToConsultants}
                    onCheckedChange={(checked) => setOptions({ ...options, assignToConsultants: checked as boolean })}
                  />
                  <Label htmlFor="assignToConsultants">Assign stories to consultants from config</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="addAcceptanceCriteria"
                    checked={options.addAcceptanceCriteria}
                    onCheckedChange={(checked) => setOptions({ ...options, addAcceptanceCriteria: checked as boolean })}
                  />
                  <Label htmlFor="addAcceptanceCriteria">Add acceptance criteria to stories</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="onlyReadyItems"
                    checked={options.onlyReadyItems}
                    onCheckedChange={(checked) => setOptions({ ...options, onlyReadyItems: checked as boolean })}
                  />
                  <Label htmlFor="onlyReadyItems">Only include items marked "Ready for Implementation"</Label>
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Button
                  onClick={() => setStep('credentials')}
                  variant="outline"
                >
                  Back
                </Button>

                <Button
                  onClick={loadPreview}
                  disabled={loadingPreview}
                >
                  {loadingPreview ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading Preview...
                    </>
                  ) : (
                    'Next: Preview'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Review what will be created in Jira before generating the backlog.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Epics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl">{preview.epicCount}</div>
                    <p className="text-sm text-gray-500 mt-1">One per phase</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Stories
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl">{preview.storyCount}</div>
                    <p className="text-sm text-gray-500 mt-1">One per config item</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ListTodo className="w-4 h-4" />
                      Subtasks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl">{preview.subtaskCount}</div>
                    <p className="text-sm text-gray-500 mt-1">Detailed implementation steps</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Story Points</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl">{preview.estimatedStoryPoints}</div>
                    <p className="text-sm text-gray-500 mt-1">Estimated effort</p>
                  </CardContent>
                </Card>
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-800">
                  <strong>Target Project:</strong> {credentials.jiraUrl}/browse/{credentials.projectKey}
                </AlertDescription>
              </Alert>

              <Separator />

              <div className="flex items-center justify-between">
                <Button
                  onClick={() => setStep('options')}
                  variant="outline"
                >
                  Back
                </Button>

                <Button
                  onClick={generateBacklog}
                  disabled={generating}
                >
                  Generate Backlog
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Generating */}
          {step === 'generating' && (
            <div className="space-y-4">
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Generating Jira backlog... This may take a few minutes.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Progress</span>
                  <span>{generationProgress}%</span>
                </div>
                <Progress value={generationProgress} />
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p>• Creating Epics for each phase...</p>
                <p>• Creating Stories for config items...</p>
                <p>• Creating Subtasks for detailed steps...</p>
                <p>• Setting up labels and components...</p>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 'complete' && generationResult && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Backlog generated.</strong>
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-1">{generationResult.epics?.length || 0}</div>
                  <div className="text-sm text-gray-600">Epics Created</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-1">{generationResult.stories?.length || 0}</div>
                  <div className="text-sm text-gray-600">Stories Created</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-1">{generationResult.subtasks?.length || 0}</div>
                  <div className="text-sm text-gray-600">Subtasks Created</div>
                </div>
              </div>

              {generationResult.errors && generationResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Some errors occurred:</strong>
                    <ul className="mt-2 list-disc list-inside">
                      {generationResult.errors.slice(0, 5).map((error: string, idx: number) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep('credentials');
                    setGenerationResult(null);
                    setGenerationProgress(0);
                  }}
                >
                  Generate Another Backlog
                </Button>

                <Button
                  onClick={() => {
                    window.open(`${credentials.jiraUrl}/browse/${credentials.projectKey}`, '_blank');
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View in Jira
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}